import { useCallback, useState } from 'react';
import { useCanvas } from '../contexts/CanvasContext';
import { useDesignerStore } from '../stores';
import { useHistoryStore } from '../stores/historyStore';
import type { Template } from '../types/template';
import type { PrintArea } from '../types/canvas';

// Import fabric for side effects
import 'fabric';

interface UseTemplateReturn {
  loadTemplate: (template: Template) => Promise<void>;
  clearTemplate: () => void;
  isLoading: boolean;
  error: string | null;
}

const DEFAULT_PRINT_AREA: PrintArea = {
  x: 100,
  y: 100,
  width: 600,
  height: 600,
  shape: 'rect',
};

export const useTemplate = (): UseTemplateReturn => {
  const { canvas } = useCanvas();
  const { setTemplate, setPrintArea, setLaserOnly } = useDesignerStore();
  const { clear: clearHistory, saveSnapshot } = useHistoryStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load an image onto the canvas
  const loadImage = useCallback((
    url: string,
    options: Partial<fabric.Image> & {
      isBaseImage?: boolean;
      isMaskOverlay?: boolean;
    }
  ): Promise<fabric.Image> => {
    return new Promise((resolve, reject) => {
      const f = window.fabric;

      f.Image.fromURL(url, (img) => {
        if (!img) {
          reject(new Error('Failed to load image'));
          return;
        }

        img.set({
          ...options,
          selectable: false,
          evented: false,
        });

        resolve(img);
      }, { crossOrigin: 'anonymous' });
    });
  }, []);


  // Load template onto canvas
  const loadTemplate = useCallback(async (template: Template): Promise<void> => {
    if (!canvas) {
      setError('Canvas not initialized');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Clear existing objects (except user content if needed)
      const objectsToRemove = canvas.getObjects().filter(
        (obj) => obj.isBaseImage || obj.isMaskOverlay || obj.isGuide
      );
      objectsToRemove.forEach((obj) => canvas.remove(obj));

      // Set canvas dimensions
      canvas.setWidth(template.width);
      canvas.setHeight(template.height);

      // Determine print area
      const printArea = template.printArea || DEFAULT_PRINT_AREA;
      setPrintArea(printArea);

      // Set laser mode if specified
      if (template.laserOnly !== undefined) {
        setLaserOnly(template.laserOnly);
      }

      // Load base image (product mockup)
      if (template.baseImage) {
        const baseImg = await loadImage(template.baseImage, {
          isBaseImage: true,
          isMockup: true,
          left: template.width / 2,
          top: template.height / 2,
          originX: 'center',
          originY: 'center',
        });

        // Scale to fit canvas
        const scale = Math.min(
          template.width / (baseImg.width || template.width),
          template.height / (baseImg.height || template.height)
        );
        baseImg.set({ scaleX: scale, scaleY: scale });

        canvas.add(baseImg);
        canvas.sendToBack(baseImg);
      }

      // Analyze clippedMask to create clipPath from OPAQUE pixels
      const f = window.fabric;

      if (template.clippedMask) {
        const maskImg = new Image();
        maskImg.crossOrigin = 'anonymous';

        await new Promise<void>((resolve) => {
          maskImg.onload = () => {
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = maskImg.width;
            tempCanvas.height = maskImg.height;
            const ctx = tempCanvas.getContext('2d');
            if (!ctx) {
              resolve();
              return;
            }

            ctx.drawImage(maskImg, 0, 0);
            const imageData = ctx.getImageData(0, 0, maskImg.width, maskImg.height);
            const data = imageData.data;

            // Find OPAQUE pixels (alpha >= 128) - these form the design area
            let minX = maskImg.width, minY = maskImg.height, maxX = 0, maxY = 0;

            for (let y = 0; y < maskImg.height; y++) {
              for (let x = 0; x < maskImg.width; x++) {
                const i = (y * maskImg.width + x) * 4;
                const alpha = data[i + 3] || 0;

                if (alpha >= 128) {
                  minX = Math.min(minX, x);
                  minY = Math.min(minY, y);
                  maxX = Math.max(maxX, x);
                  maxY = Math.max(maxY, y);
                }
              }
            }

            // Scale bounds to canvas
            const scale = Math.min(canvas.width! / maskImg.width, canvas.height! / maskImg.height);
            const offsetX = (canvas.width! - maskImg.width * scale) / 2;
            const offsetY = (canvas.height! - maskImg.height * scale) / 2;

            const clipWidth = (maxX - minX) * scale;
            const clipHeight = (maxY - minY) * scale;
            const clipLeft = minX * scale + offsetX;
            const clipTop = minY * scale + offsetY;

            const radius = Math.min(clipWidth, clipHeight) / 2;
            const centerX = clipLeft + clipWidth / 2;
            const centerY = clipTop + clipHeight / 2;

            const clipPathShape = new f.Circle({
              left: centerX,
              top: centerY,
              radius: radius,
              originX: 'center',
              originY: 'center',
              absolutePositioned: true,
            });

            (canvas as any)._designClipPath = clipPathShape;
            resolve();
          };
          maskImg.onerror = () => resolve();
          maskImg.src = template.clippedMask!;
        });
      } else {
        (canvas as any)._designClipPath = null;
      }

      // Load alpha mask overlay (transparent center, opaque edges)
      if (template.alphaMask) {
        const alphaMaskImg = await loadImage(template.alphaMask, {
          isMaskOverlay: true,
          isMockup: true,
          left: template.width / 2,
          top: template.height / 2,
          originX: 'center',
          originY: 'center',
        });

        // Scale to match base image
        const scale = Math.min(
          template.width / (alphaMaskImg.width || template.width),
          template.height / (alphaMaskImg.height || template.height)
        );
        alphaMaskImg.set({ scaleX: scale, scaleY: scale });

        canvas.add(alphaMaskImg);
        canvas.bringToFront(alphaMaskImg);
      }

      // Store template with print area
      const templateWithPrintArea: Template = {
        ...template,
        printArea,
      };

      setTemplate(templateWithPrintArea);

      // Clear history and save initial state
      clearHistory();
      const state = JSON.stringify(
        canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay'])
      );
      saveSnapshot(state);

      // Force re-render and re-calculate dimensions
      canvas.calcOffset();
      canvas.renderAll();

      // Trigger a second render after a short delay to ensure proper centering
      setTimeout(() => {
        canvas.calcOffset();
        canvas.renderAll();
      }, 100);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load template';
      setError(message);
      console.error('Template loading error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [canvas, loadImage, setTemplate, setPrintArea, setLaserOnly, clearHistory, saveSnapshot]);

  // Clear template and reset canvas
  const clearTemplate = useCallback(() => {
    if (!canvas) return;

    // Remove all objects
    canvas.clear();
    canvas.clipPath = undefined;
    canvas.backgroundColor = '#ffffff';

    // Reset store state
    setTemplate(null);
    setPrintArea(DEFAULT_PRINT_AREA);
    setLaserOnly(false);

    // Clear history
    clearHistory();

    canvas.renderAll();
  }, [canvas, setTemplate, setPrintArea, setLaserOnly, clearHistory]);

  return {
    loadTemplate,
    clearTemplate,
    isLoading,
    error,
  };
};

export default useTemplate;
